import i18n from 'i18n/config';
import { formatDateAndConvertInLocalTime, formatDateTimeAndConvertInLocalTime } from 'shared/utils/common';
import type { ExportContext } from './exportMRT';

/**
 * Reusable `meta` values for MRT column definitions, so that a column whose displayed text is
 * produced inside its `Cell` renderer exports that same text instead of the raw field.
 *
 * Attach as `meta: translatedExport` on the column definition.
 */

const isBlank = (value: unknown) =>
    value === null || value === undefined || value === '' || value === ' - ' || value === '-';

/**
 * For reference-data columns whose grid `Cell` shows " - " for an empty value 
 * (name/description in the default language and every translation column)
 */
export const dashWhenEmptyExport = {
    exportFn: (_row: unknown, ctx: ExportContext) =>
        isBlank(ctx.value) ? ' - ' : ctx.value,
};

/**
 * For columns rendered with `CellRenderer` / `SmartCellRenderer`, or any `Cell` that renders
 * `t(value)`. Exports the translated label rather than the raw enum/code.
 */
export const translatedExport = {
    exportFn: (_row: unknown, ctx: ExportContext) =>
        isBlank(ctx.value) ? '' : String(i18n.t(String(ctx.value))),
};

/**
 * For columns that show a checkmark/cross icon for a boolean. Reads the raw row field rather than
 * `ctx.value`, because these columns often build the icon inside `accessorFn` itself - which would
 * otherwise make every row look truthy.
 */
export const booleanExport = {
    exportFn: (row: any, ctx: ExportContext) => {
        const raw = row?.[ctx.accessorKey] ?? ctx.value;
        return String(i18n.t(raw ? 'YES' : 'NO'));
    },
};

/** For columns whose `accessorFn` returns JSX; exports the plain underlying field instead. */
export const rawFieldExport = (field?: string) => ({
    exportFn: (row: any, ctx: ExportContext) => {
        const raw = row?.[field ?? ctx.accessorKey];
        return isBlank(raw) ? '' : raw;
    },
});

/** For columns whose `Cell` renders a date without a time component. */
export const dateExport = {
    exportFn: (_row: unknown, ctx: ExportContext) =>
        isBlank(ctx.value) ? '' : formatDateAndConvertInLocalTime(ctx.value as Date),
};

/** For columns whose `Cell` renders both date and time. */
export const dateTimeExport = {
    exportFn: (_row: unknown, ctx: ExportContext) =>
        isBlank(ctx.value) ? '' : formatDateTimeAndConvertInLocalTime(ctx.value as Date),
};

/**
 * For columns whose `Cell` appends the row's currency to a numeric amount. Keeps the amount
 * numeric in the spreadsheet and puts the currency in its own trailing token only when present.
 */
export const amountWithCurrencyExport = (currencyField = 'currency') => ({
    exportFn: (row: any, ctx: ExportContext) => {
        if (isBlank(ctx.value)) return '';
        const currency = row?.[currencyField];
        return currency ? `${ctx.value} ${currency}` : ctx.value;
    },
});

/**
 * For columns whose `Cell` resolves an id against a `{ label, value }` option list. Pass a getter
 * because the option list usually comes from redux and is only available at render time.
 */
export const optionLabelExport = <T>(getOptions: () => { label: string; value: T }[] | undefined) => ({
    exportFn: (_row: unknown, ctx: ExportContext) => {
        if (isBlank(ctx.value)) return '';
        const match = getOptions()?.find(option => String(option.value) === String(ctx.value));
        return match ? String(i18n.t(match.label)) : '';
    },
});

/**
 * For columns whose `Cell` renders the translated *name* of a numeric TS enum member
 * (e.g. `t(AccountingNumberType[value])`) while the raw field is the number.
 */
export const enumNameExport = (enumObject: Record<number, string>) => ({
    exportFn: (_row: unknown, ctx: ExportContext) => {
        if (isBlank(ctx.value)) return '';
        const name = enumObject[Number(ctx.value)];
        return name ? String(i18n.t(name)) : '';
    },
});

/** For columns whose `Cell` joins an array of objects into readable text. */
export const joinedExport = (pick: (item: any) => unknown, separator = ', ') => ({
    exportFn: (_row: unknown, ctx: ExportContext) => {
        const list = ctx.value;
        if (!Array.isArray(list)) return isBlank(list) ? '' : String(list);
        return list.map(pick).filter(item => !isBlank(item)).join(separator);
    },
});

/**
 * For `approvalList` columns, which render <ApprovalList> - initials in the grid, full names in
 * its tooltip. The spreadsheet has no tooltip, so export the full names.
 */
export const approvalListExport = {
    exportFn: (_row: unknown, ctx: ExportContext) => {
        const list = ctx.value;
        if (!Array.isArray(list)) return '';
        return list
            .map((person: any) => person?.fullName ?? person?.abbreviation)
            .filter(name => !isBlank(name))
            .join(', ');
    },
};

/**
 * For date columns that show a time component only for some rows (e.g. hour-based absences).
 */
export const dateOrDateTimeExport = (includeTime: (row: any) => boolean) => ({
    exportFn: (row: any, ctx: ExportContext) => {
        if (isBlank(ctx.value)) return '';
        return includeTime(row)
            ? formatDateTimeAndConvertInLocalTime(ctx.value as Date)
            : formatDateAndConvertInLocalTime(ctx.value as Date);
    },
});
